/** Shared design size presets for New design / program flows. */

export type DesignSizePreset = {
  id: string;
  label: string;
  /** Pixel width (after unit conversion when unit !== 'px') */
  width: number;
  height: number;
  unit: 'px' | 'in' | 'cm';
  /** When unit is in/cm, dpi used to convert to px (default store uses 72). */
  dpi?: number;
  /** Create this many blank pages (default 1). */
  pages?: number;
  hint?: string;
};

export type DesignSizeCategory = {
  id: string;
  name: string;
  presets: DesignSizePreset[];
};

/** Half-letter folded program panel (~5.5" × 8.5" @ 150 dpi). */
export const PROGRAM_PANEL_W = 825;
export const PROGRAM_PANEL_H = 1275;

export const PROGRAM_LABELS = ['Front', 'Inside L', 'Inside R', 'Back'] as const;

/** Print/read order indices: 1, 4, 2, 3 */
export const PROGRAM_EXPORT_ORDER = [0, 3, 1, 2] as const;

export const DESIGN_SIZE_CATEGORIES: DesignSizeCategory[] = [
  {
    id: 'instagram',
    name: 'Instagram',
    presets: [
      { id: 'ig-post', label: 'Post', width: 1080, height: 1080, unit: 'px' },
      { id: 'ig-story', label: 'Story', width: 1080, height: 1920, unit: 'px' },
      { id: 'ig-reels', label: 'Reels', width: 1080, height: 1920, unit: 'px', hint: 'Same as Story / TikTok' },
    ],
  },
  {
    id: 'facebook',
    name: 'Facebook',
    presets: [
      { id: 'fb-post', label: 'Post', width: 1200, height: 630, unit: 'px' },
      { id: 'fb-square', label: 'Post (Square)', width: 1080, height: 1080, unit: 'px' },
      { id: 'fb-cover', label: 'Cover', width: 851, height: 315, unit: 'px' },
    ],
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    presets: [
      { id: 'tt-video', label: 'Video / Reels', width: 1080, height: 1920, unit: 'px' },
    ],
  },
  {
    id: 'youtube',
    name: 'YouTube',
    presets: [
      { id: 'yt-thumb', label: 'Thumbnail', width: 1280, height: 720, unit: 'px' },
      { id: 'yt-short', label: 'Short', width: 1080, height: 1920, unit: 'px' },
    ],
  },
  {
    id: 'print',
    name: 'Print',
    presets: [
      {
        id: 'letter',
        label: 'Letter 8.5×11"',
        width: 8.5,
        height: 11,
        unit: 'in',
        dpi: 300,
        hint: '2550×3300 px @ 300 dpi',
      },
      {
        id: 'letter-land',
        label: 'Letter landscape',
        width: 11,
        height: 8.5,
        unit: 'in',
        dpi: 300,
        hint: '3300×2550 px @ 300 dpi',
      },
      {
        id: 'program-panel',
        label: 'Program panel',
        width: PROGRAM_PANEL_W,
        height: PROGRAM_PANEL_H,
        unit: 'px',
        hint: 'Half-letter folded panel (~5.5×8.5")',
      },
      {
        id: 'program-4',
        label: 'Program 4-page',
        width: PROGRAM_PANEL_W,
        height: PROGRAM_PANEL_H,
        unit: 'px',
        pages: 4,
        hint: 'Front · Inside L · Inside R · Back',
      },
      {
        id: 'cover',
        label: 'Cover (letter)',
        width: 8.5,
        height: 11,
        unit: 'in',
        dpi: 300,
        hint: 'Full-page cover at letter size',
      },
    ],
  },
];

/** Convert unit value to CSS pixels (matches OpenPolotno unitToPx). */
export function unitToPx(unitVal: number, unit: string, dpi: number): number {
  if (unit === 'px') return Math.round(unitVal);
  if (unit === 'in') return Math.round(unitVal * dpi);
  if (unit === 'cm') return Math.round((unitVal * dpi) / 2.54);
  if (unit === 'mm') return Math.round((unitVal * dpi) / 25.4);
  return Math.round(unitVal);
}

export function presetToPixels(preset: DesignSizePreset): { width: number; height: number; dpi: number; unit: string } {
  const dpi = preset.dpi ?? (preset.unit === 'px' ? 72 : 300);
  return {
    width: unitToPx(preset.width, preset.unit, dpi),
    height: unitToPx(preset.height, preset.unit, dpi),
    dpi,
    unit: preset.unit === 'px' ? 'px' : preset.unit,
  };
}

export function formatSizeLabel(width: number, height: number): string {
  return `${Math.round(width)}×${Math.round(height)} px`;
}
